import { SectionHeader } from "./SectionHeader";
import { CopyCommand } from "./CopyCommand";

const TermLine = ({ children, className = "" }) => (
    <div className={`whitespace-pre-wrap break-words ${className}`}>{children}</div>
);

const Terminal = () => (
    <div
        data-testid="setup-terminal"
        className="overflow-hidden border border-[#262626] bg-black font-mono text-xs leading-6 sm:text-[13px]"
    >
        <div className="flex items-center gap-2 border-b border-[#262626] bg-[#0a0a0a] px-4 py-2.5">
            <span className="h-3 w-3 rounded-full bg-[#ff5f57]" />
            <span className="h-3 w-3 rounded-full bg-[#febc2e]" />
            <span className="h-3 w-3 rounded-full bg-[#28c840]" />
            <span className="ml-3 text-[10px] tracking-[0.2em] text-neutral-600">zsh — remembered</span>
        </div>
        <div className="p-5">
            <TermLine>
                <span className="text-green-400">$</span> <span className="text-neutral-100">npx remembered-cli</span>
            </TermLine>
            <TermLine className="mt-2 text-neutral-400">remembered 1.0.0</TermLine>
            <TermLine className="text-neutral-500">procedural memory for agents, stored on your own machine</TermLine>
            <TermLine className="mt-3 text-neutral-500">commands</TermLine>
            <TermLine className="text-neutral-400">
                {"  setup     init setup login install-hooks agents-md"}
            </TermLine>
            <TermLine className="text-neutral-400">
                {"  memory    record ingest backfill recall show list chain"}
            </TermLine>
            <TermLine className="text-neutral-400">
                {"  upkeep    prune enable disable forget flush"}
            </TermLine>
            <TermLine className="mt-3">
                <span className="text-green-400">$</span> <span className="text-neutral-100">remembered login</span>
                <span className="text-neutral-600">  # opens a browser, approves this machine</span>
            </TermLine>
            <TermLine>
                <span className="text-green-400">$</span> <span className="text-neutral-100">remembered install-hooks</span>
                <span className="text-neutral-600">  # recall runs on every new prompt</span>
            </TermLine>
            <TermLine>
                <span className="text-green-400">$</span> <span className="text-neutral-100">remembered enable</span>
                <span className="text-neutral-600">  # explicit write consent, nothing stored before this</span>
            </TermLine>
            <TermLine className="mt-2">
                <span className="text-green-400">$</span>{" "}
                <span className="text-neutral-100">remembered recall "fix the failing auth test"</span>
            </TermLine>
            <TermLine className="text-amber-400">
                0.866 procedures/89f11bab-fix-failing-auth-test [lexical,semantic]
            </TermLine>
            <TermLine className="mt-2">
                <span className="text-green-400">$</span> <span className="text-neutral-100">remembered disable</span>
                <span className="text-neutral-600">  # read-only. recall works, nothing new is recorded</span>
            </TermLine>
            <TermLine className="mt-2">
                <span className="text-green-400">$</span> <span className="caret-blink text-green-400">▊</span>
            </TermLine>
        </div>
    </div>
);

const STEPS = [
    {
        n: "01",
        title: "Install in one command",
        desc: "Point it at Claude Code, Codex, Cursor, or any other harness you run.",
    },
    {
        n: "02",
        title: "Set up once",
        desc: "Run the commands on the left to set up and sign in. remembered.sh then works in the background.",
    },
    {
        n: "03",
        title: "Create new memories",
        desc: "Capture every workflow your agents run or recall as a reusable procedure.",
    },
];

const Setup = () => (
    <section id="setup" data-testid="setup-section" className="border-t border-[#262626]">
        <div className="mx-auto max-w-6xl px-4 py-20 sm:px-6 sm:py-28">
            <SectionHeader id="setup">Setup In Three Steps</SectionHeader>
            <div className="grid gap-10 lg:grid-cols-[1.2fr_1fr] lg:gap-14">
                <div>
                    <Terminal />
                    <div className="mt-5">
                        <CopyCommand testid="setup-copy-install-button" />
                    </div>
                </div>
                <ol className="flex flex-col gap-px border border-[#262626] bg-[#262626] self-start">
                    {STEPS.map((step) => (
                        <li
                            key={step.n}
                            data-testid={`setup-step-${step.n}`}
                            className="group bg-[#050505] p-6 transition-colors duration-200 hover:bg-[#0a0a0a] sm:p-8"
                        >
                            <span className="font-mono text-xs tracking-[0.3em] text-neutral-600 transition-colors group-hover:text-green-400">
                                {step.n}
                            </span>
                            <h3 className="mt-3 text-xl font-medium text-neutral-100">{step.title}</h3>
                            <p className="mt-2 text-sm leading-relaxed text-neutral-400">{step.desc}</p>
                        </li>
                    ))}
                </ol>
            </div>
        </div>
    </section>
);

export default Setup;
